import { useState } from 'react';
import './assignedDetails.css';

export default function AssignedDetails({
    user,
    assignmentHistory = [],
    supportUsers = [],
    isSuperAdmin = false,
    assigning = false,
    onAssign
}) {
    const [showModal, setShowModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [searchText, setSearchText] = useState('');

    const assignedUser = user?.assignedSupportId;

    // =========================================
    // ASSIGN
    // =========================================

    const handleAssign = async () => {
        if (!selectedUserId || !onAssign) return;

        await onAssign(selectedUserId);

        setSelectedUserId('');
        setSearchText('');
        setShowModal(false);
    };

    // =========================================
    // CLOSE MODAL
    // =========================================

    const closeModal = () => {
        setSelectedUserId('');
        setSearchText('');
        setShowModal(false);
    };

    // =========================================
    // HELPERS
    // =========================================

    const formatDate = (date) => {
        if (!date) return '-';

        return new Date(date).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getInitials = (name = '') => {
        return name
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((item) => item[0])
            .join('')
            .toUpperCase();
    };

    // =========================================
    // FILTER SUPPORT USERS
    // =========================================

    const filteredUsers = supportUsers.filter((supportUser) => {
        const currentAssignedId = assignedUser?._id?.toString();

        const supportUserId = supportUser?._id?.toString();

        // Don't show currently assigned user
        if (currentAssignedId && supportUserId === currentAssignedId) {
            return false;
        }

        const search = searchText.toLowerCase().trim();

        if (!search) return true;

        return (
            supportUser?.name?.toLowerCase().includes(search) ||
            supportUser?.emailId?.toLowerCase().includes(search) ||
            String(supportUser?.phoneNumber || '').includes(search)
        );
    });

    return (
        <>
            <div className='assignedSupportContainer'>
                {/* =========================================
                    CURRENT ASSIGNMENT
                ========================================= */}

                <div className='assignedSupportCard'>
                    <div className='assignedSupportHeader'>
                        <h3>Assigned Support User</h3>

                        {isSuperAdmin && assignedUser && (
                            <button type='button' className='assignSupportBtn' onClick={() => setShowModal(true)}>
                                <span className='btnEditIcon'>✎</span>

                                {assignedUser ? 'Change Support User' : 'Assign Support User'}
                            </button>
                        )}
                    </div>

                    {assignedUser ? (
                        <>
                            <div className='assignedUserDetails'>
                                {/* =====================================
                                    USER
                                ===================================== */}

                                <div className='assignedUserMain'>
                                    <div className='assignedAvatar'>{getInitials(assignedUser?.name)}</div>

                                    <div className='assignedUserInfo'>
                                        <div className='assignedNameRow'>
                                            <h4>{assignedUser?.name || '-'}</h4>

                                            <span className='activeBadge'>Active</span>
                                        </div>

                                        <p>{assignedUser?.emailId || '-'}</p>

                                        {assignedUser?.phoneNumber && (
                                            <p>
                                                {assignedUser?.countryCode} {assignedUser?.phoneNumber}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* =====================================
                                    ASSIGNED ON
                                ===================================== */}

                                <div className='assignmentMetaItem'>
                                    <div className='metaIcon'>📅</div>

                                    <div>
                                        <span>Assigned On</span>

                                        <strong>{formatDate(user?.assignedAt)}</strong>
                                    </div>
                                </div>

                                {/* =====================================
                                    ASSIGNMENT TYPE
                                ===================================== */}

                                <div className='assignmentMetaItem'>
                                    <div className='metaIcon'>👥</div>

                                    <div>
                                        <span>Assignment Type</span>

                                        <strong>
                                            {user?.assignmentType === 'automatic' ? 'Round Robin' : 'Manual'}
                                        </strong>
                                    </div>
                                </div>

                                {/* =====================================
                                    ASSIGNED BY
                                ===================================== */}

                                <div className='assignmentMetaItem'>
                                    <div className='metaIcon'>👤</div>

                                    <div>
                                        <span>Assigned By</span>

                                        <strong>{user?.assignedBy?.name || 'System'}</strong>

                                        {user?.assignedBy?.access && (
                                            <small
                                                className='assignmentMetaRole'
                                                style={{
                                                    textTransform: 'capitalize'
                                                }}
                                            >
                                                {user.assignedBy.access}
                                            </small>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* =========================================
                                AUTOMATIC INFO
                            ========================================= */}

                            {user?.assignmentType === 'automatic' && (
                                <div className='automaticAssignmentInfo'>
                                    <span className='infoIcon'>i</span>

                                    <span>
                                        This user was automatically assigned via round robin when they sent a message.
                                    </span>
                                </div>
                            )}
                        </>
                    ) : (
                        /* =========================================
                           NO ASSIGNED USER
                        ========================================= */

                        <div className='noAssignedUser'>
                            <div>
                                <strong>No support user assigned</strong>

                                <p>This lead is currently unassigned.</p>
                            </div>

                            {isSuperAdmin && (
                                <button type='button' className='assignSupportBtn' onClick={() => setShowModal(true)}>
                                    Assign Support User
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* =========================================
                    ASSIGNMENT HISTORY
                ========================================= */}

                {isSuperAdmin && (
                    <div className='assignmentHistoryCard'>
                        <div className='assignmentHistoryHeader'>
                            <h3>Assignment History</h3>
                        </div>

                        {assignmentHistory.length === 0 ? (
                            <div className='emptyHistory'>No assignment history found.</div>
                        ) : (
                            <div className='assignmentTimeline'>
                                {assignmentHistory.map((item, index) => {
                                    const isLatest = index === 0;

                                    return (
                                        <div className='assignmentHistoryItem' key={item._id}>
                                            <div className={`timelineDot ${isLatest ? 'latest' : ''}`} />

                                            <div className='historyContent'>
                                                <div className='historyTopRow'>
                                                    <div>
                                                        <h4>
                                                            {item.fromSupportId
                                                                ? `Reassigned to ${item.toSupportId?.name || '-'}`
                                                                : `Assigned to ${item.toSupportId?.name || '-'}`}
                                                        </h4>

                                                        <p>
                                                            {item.assignmentType === 'automatic'
                                                                ? 'Automatically assigned via system'
                                                                : `By ${
                                                                      item.assignedBy?.name || 'Super Admin'
                                                                  } (Manually)`}
                                                        </p>
                                                    </div>

                                                    <span className='historyDate'>{formatDate(item.createdAt)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* =========================================
                ASSIGNMENT MODAL
            ========================================= */}

            <AssignmentModal
                showModal={showModal}
                closeModal={closeModal}
                assignedUser={assignedUser}
                user={user}
                filteredUsers={filteredUsers}
                searchText={searchText}
                setSearchText={setSearchText}
                selectedUserId={selectedUserId}
                setSelectedUserId={setSelectedUserId}
                assigning={assigning}
                handleAssign={handleAssign}
                getInitials={getInitials}
                formatDate={formatDate}
            />
        </>
    );
}

// =====================================================
// ASSIGNMENT MODAL
// =====================================================

export const AssignmentModal = ({
    showModal,
    closeModal,
    assignedUser,
    user,
    filteredUsers,
    searchText,
    setSearchText,
    selectedUserId,
    setSelectedUserId,
    assigning,
    handleAssign,
    getInitials,
    formatDate
}) => {
    if (!showModal) return null;

    return (
        <div className='assignmentModalOverlay' onClick={closeModal}>
            <div className='assignmentModal' onClick={(e) => e.stopPropagation()}>
                {/* =========================================
                    MODAL HEADER
                ========================================= */}

                <div className='assignmentModalHeader'>
                    <div>
                        <h3>{assignedUser ? 'Change Support User' : 'Assign Support User'}</h3>
                    </div>

                    <button type='button' className='modalCloseBtn' onClick={closeModal}>
                        ×
                    </button>
                </div>

                {/* =========================================
                    CURRENT ASSIGNMENT
                ========================================= */}

                {assignedUser && (
                    <div className='currentAssignmentBox'>
                        {/* USER */}
                        <div className='currentAssignmentAvatar'>{getInitials(assignedUser?.name)}</div>

                        <div className='currentAssignmentInfo'>
                            <span>Currently Assigned To</span>

                            <strong>{assignedUser?.name}</strong>

                            <p>
                                {assignedUser?.phoneNumber
                                    ? `${assignedUser?.countryCode || ''} ${assignedUser?.phoneNumber}`
                                    : assignedUser?.emailId}
                            </p>
                        </div>

                        {/* ASSIGNED BY */}
                        <div className='currentAssignmentMeta'>
                            <span>Assigned By</span>

                            {user?.assignedBy ? (
                                <>
                                    <strong>{user.assignedBy.name}</strong>

                                    {user.assignedBy.access && (
                                        <small
                                            className='assignmentMetaRole'
                                            style={{
                                                textTransform: 'capitalize'
                                            }}
                                        >
                                            {user.assignedBy.access}
                                        </small>
                                    )}
                                </>
                            ) : (
                                <strong>System</strong>
                            )}

                            <small
                                style={{
                                    textTransform: 'capitalize'
                                }}
                            >
                                {user?.assignmentType === 'automatic' ? 'Round Robin' : 'Manual'}
                            </small>
                        </div>

                        {/* ASSIGNED ON */}
                        <div className='currentAssignmentMeta'>
                            <span>📅 Assigned On</span>

                            <strong>{formatDate(user?.assignedAt)}</strong>
                        </div>
                    </div>
                )}

                {/* =========================================
                    MODAL BODY
                ========================================= */}

                <div className='assignmentModalBody'>
                    <label>Select New Support User</label>

                    {/* SEARCH */}
                    <div className='supportSearchBox'>
                        <span className='searchIcon'>🔍</span>

                        <input
                            type='text'
                            placeholder='Search support user by name, email or phone...'
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </div>

                    {/* =====================================
                        SUPPORT USER LIST
                    ===================================== */}

                    <div className='supportUserList'>
                        {filteredUsers.length === 0 ? (
                            <div className='noSupportUsers'>No support users found.</div>
                        ) : (
                            filteredUsers.map((supportUser) => {
                                const isSelected = selectedUserId === supportUser._id;

                                return (
                                    <button
                                        type='button'
                                        key={supportUser._id}
                                        className={`supportUserOption ${isSelected ? 'selected' : ''}`}
                                        onClick={() => setSelectedUserId(supportUser._id)}
                                    >
                                        {/* AVATAR */}
                                        <div className='supportUserAvatar'>{getInitials(supportUser?.name)}</div>

                                        {/* USER INFO */}
                                        <div className='supportUserInfo'>
                                            <strong>{supportUser?.name}</strong>

                                            <span>
                                                {supportUser?.phoneNumber
                                                    ? `${supportUser?.countryCode || ''} ${supportUser?.phoneNumber}`
                                                    : supportUser?.emailId}
                                            </span>
                                        </div>

                                        {/* RADIO */}
                                        <div className={`radioCircle ${isSelected ? 'checked' : ''}`}>
                                            {isSelected && <span />}
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* =========================================
                    FOOTER
                ========================================= */}

                <div className='assignmentModalFooter'>
                    <button type='button' className='cancelAssignBtn' onClick={closeModal}>
                        Cancel
                    </button>

                    <button
                        type='button'
                        className='confirmAssignBtn'
                        disabled={!selectedUserId || assigning}
                        onClick={handleAssign}
                    >
                        {assigning ? 'Saving...' : assignedUser ? 'Reassign' : 'Assign'}
                    </button>
                </div>
            </div>
        </div>
    );
};
